import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UploadCloud, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PLATFORMS } from "@/lib/ui";

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-colors";

export default function ImportListingsDialog({ open, onOpenChange, clients, onImported }) {
  const [brand, setBrand] = useState("");
  const [platform, setPlatform] = useState("Flipkart");
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!brand) return toast.error("Select a brand");
    if (!file) return toast.error("Choose a CSV or Excel file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/imports/listings?brand=${encodeURIComponent(brand)}&platform=${encodeURIComponent(platform)}`, fd);
      toast.success(`Imported ${data.total_rows} SKUs → 1 catalog task + ${data.sku_tasks_created} listing tasks`);
      onOpenChange(false);
      setBrand(""); setFileName("");
      if (fileRef.current) fileRef.current.value = "";
      onImported && onImported();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Bulk Import Product Listings</DialogTitle>
          <DialogDescription className="sr-only">Upload a product spreadsheet to auto-create catalog and per-SKU listing tasks.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="import-brand-select" className={inputCls} value={brand} onChange={(e) => setBrand(e.target.value)}>
              <option value="">Select brand *</option>
              {clients.map((c) => <option key={c.id} value={c.brand}>{c.brand}</option>)}
            </select>
            <select data-testid="import-platform-select" className={inputCls} value={platform} onChange={(e) => setPlatform(e.target.value)}>
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <button
            type="button"
            data-testid="import-file-picker"
            onClick={() => fileRef.current?.click()}
            className="w-full border border-dashed border-white/15 rounded-sm p-6 text-center hover:border-white/30 hover:bg-white/[0.02] transition-colors das-grid"
          >
            <UploadCloud size={26} className="mx-auto text-zinc-500" />
            <div className="text-sm text-zinc-300 mt-2">{fileName || "Click to choose a CSV or Excel file"}</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-600 mt-1">.csv · .xlsx</div>
          </button>
          <input ref={fileRef} data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />

          <div className="flex items-start gap-2 text-xs text-zinc-500 bg-[#0a0a0a] border border-white/10 rounded-sm p-3">
            <FileSpreadsheet size={15} className="mt-0.5 shrink-0" />
            <span>Creates one catalog task (with total SKU count + the attached file) plus one Product Listing task per row. A column named Product / Title / Name / SKU is used for task titles.</span>
          </div>

          <button data-testid="import-submit-button" onClick={submit} disabled={busy} className="w-full flex items-center justify-center gap-2 text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98] disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} {busy ? "Importing…" : "Import & Create Tasks"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
